import httpStore from "@/api/httpStore.js";

/**
 * Editor mode: suggest field improvements (title, keywords, etc.).
 * Sends only the editable fields + compact applications for context.
 * Returns { message, suggestions: [{field, label, current, suggested}], _raw }
 */
export function suggestProduct(
  prompt,
  product,
  applications = [],
  history = []
) {
  const editableFields = {
    nkrCode: product.nkrCode,
    brand: product.brand,
    title: product.title,
    titleStd: product.titleStd,
    titleAlt: product.titleAlt,
    keywords: product.keywords,
    infoautoTitle: product.description,
    longDescription: product.longDescription,
    pack: product.pack,
    oe: product.oe
  };
  return httpStore
    .post("/ai/suggest_product", {
      prompt,
      product: editableFields,
      applications,
      history
    })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Verify VIN compatibility against all products in an order.
 * PHP fetches applications from DB for each nkrCode.
 * Returns { vinDecoded: {make, model, year, engine}, results: [{nkrCode, compatible, reason}] }
 */
export function verifyVinOrder(vin, items) {
  return httpStore
    .post("/ai/verify_vin_order", { vin, items })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Info mode: answer product/compatibility questions as a regular user.
 * Sends minimal product identity fields + full applications list.
 * Returns { message, suggestions: [], _raw }
 */
export function askProduct(prompt, product, applications = [], history = []) {
  const productInfo = {
    nkrCode: product.nkrCode,
    brand: product.brand,
    title: product.title,
    description: product.description,
    oe: product.oe,
    pack: product.pack
  };
  return httpStore
    .post("/ai/ask_product", {
      prompt,
      product: productInfo,
      applications,
      history
    })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Categorización automática en lote: dado un grupo de productos sin categoría,
 * Claude propone una subcategoría del árbol propio para cada uno.
 * El backend valida que cada id sugerido exista y NO escribe en la base:
 * devuelve sólo sugerencias para revisar y aplicar desde el panel.
 * Máximo 50 productos por llamada (el componente pagina en lotes).
 * Returns { suggestions: [{unificado, subcategoryId, categoryId, categoryName,
 *           subCategoryName, confidence, reason}], unmatched: [unificado] }
 */
export function categorizeProducts(products) {
  const payload = products.map(p => ({
    unificado: p.unificado,
    nkrCode: p.nkrCode,
    brand: p.brand,
    oe: p.oe,
    // El listado usa customTitle/originalTitle; el modal de producto, title/description.
    title: p.customTitle || p.title,
    description: p.originalTitle || p.description
  }));
  return httpStore
    .post("/ai/categorize_products", { products: payload })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Genera imágenes con Google Gemini (modelo gemini-3-pro-image) vía el backend.
 * El backend adjunta la ficha del producto al prompt cuando se manda nkrCode,
 * sube cada imagen a S3 (carpeta IA-images/) y guarda la interacción completa.
 * @param {Object} options
 * @param {string} options.prompt
 * @param {string} [options.nkrCode]       producto elegido: su data va al prompt
 * @param {string} [options.aspectRatio]   "1:1" | "16:9" | "9:16" | ...
 * @param {string} [options.imageSize]     "512" | "1K" | "2K" | "4K"
 * @param {number} [options.count]         1-4
 * @param {Array}  [options.images]        [{ mimeType, data, name }] subidas, base64 sin prefijo
 * @param {Array}  [options.referenceUrls] URLs de fotos del producto a usar como referencia
 * @param {Array}  [options.assetIds]      ids de ai_image_assets (estilos elegidos)
 * @param {boolean}[options.includeDefaultAssets] adjuntar los assets por defecto (el logo)
 * @param {number} [options.promptTemplateId] plantilla de prompt usada, para trazabilidad
 * @param {number} [options.parentImageId] iterar sobre una imagen ya generada
 * La salida siempre es JPEG: es el único formato que acepta la API de Gemini.
 * Returns { generationId, images: [{ id, url, mimeType, width, height }], usage, model }
 */
export function generateImage({
  prompt,
  nkrCode = null,
  aspectRatio = "1:1",
  imageSize = "1K",
  count = 1,
  images = [],
  referenceUrls = [],
  assetIds = [],
  includeDefaultAssets = true,
  promptTemplateId = null,
  parentImageId = null
}) {
  return httpStore
    .post("/ai/generate_image", {
      prompt,
      nkrCode,
      aspectRatio,
      imageSize,
      count,
      images,
      referenceUrls,
      assetIds,
      includeDefaultAssets,
      promptTemplateId,
      parentImageId
    })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Historial de generaciones (ai_image_generations) con sus imágenes.
 * Filtros: { mine, nkrCode, limit, offset }
 * Returns { generations: [...], total }
 */
export function getImageGenerations(filters = {}) {
  return httpStore
    .get("/ai/image_generations", { params: filters })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Biblioteca de referencias de estilo (logo, fondos, plantillas visuales).
 * Cada asset lleva un `description` que es el texto que se le manda al modelo
 * junto a la imagen para explicarle qué es.
 * Returns { assets: [...] }
 */
export function getImageAssets(all = false) {
  return httpStore
    .get("/ai/image_assets", { params: all ? { all: 1 } : {} })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Sube un asset nuevo. image: { mimeType, data } en base64 sin prefijo.
 * type: "logo" | "style" | "other"; isDefault lo adjunta a toda generación.
 */
export function createImageAsset(asset) {
  return httpStore
    .post("/ai/image_assets", asset)
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

export function updateImageAsset(id, changes) {
  return httpStore
    .put(`/ai/image_assets/${id}`, changes)
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

export function deleteImageAsset(id) {
  return httpStore
    .delete(`/ai/image_assets/${id}`)
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Plantillas de prompt reutilizables.
 * Returns { templates: [...] }
 */
export function getPromptTemplates(all = false) {
  return httpStore
    .get("/ai/prompt_templates", { params: all ? { all: 1 } : {} })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

export function createPromptTemplate(template) {
  return httpStore
    .post("/ai/prompt_templates", template)
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

export function updatePromptTemplate(id, changes) {
  return httpStore
    .put(`/ai/prompt_templates/${id}`, changes)
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

export function deletePromptTemplate(id) {
  return httpStore
    .delete(`/ai/prompt_templates/${id}`)
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

/**
 * Sugiere título, bullets y descripción para un listing de Amazon.
 * El contexto del producto (ficha, OE y aplicaciones de vehículo) lo arma el
 * backend; acá solo se manda lo que hay hoy en Amazon para que mejore sobre eso.
 */
export function suggestAmazonContent({
  nkrCode,
  currentTitle,
  currentBullets,
  currentDescription
}) {
  return httpStore
    .post("/ai/amazon_content", {
      nkrCode,
      currentTitle,
      currentBullets,
      currentDescription
    })
    .then(res => res.data);
}

/**
 * Propone un documento A+ completo: titular, texto, tabla de especificaciones y
 * qué imágenes le faltan, como prompts listos para el generador.
 * Claude decide estructura y texto; los prompts alimentan al generador (Gemini).
 */
export function suggestAplusContent(nkrCode) {
  return httpStore.post("/ai/aplus_content", { nkrCode }).then(res => res.data);
}

/**
 * Store Settings > AI models: los modelos de Claude que ve la key, cada función
 * de IA del admin con el modelo y effort que usa, y de dónde sale la key
 * (nunca la key: sólo sus últimos 4).
 * Returns { models: [{ id, displayName, createdAt, maxInputTokens, efforts,
 * supportsTools, supportsImages }], modelsError, features: [{ key, label,
 * description, requires, model, effort, defaultModel, defaultEffort }],
 * apiKey: { source: "settings"|"env"|"none", last4 } }
 */
export function getAiModels() {
  return httpStore.get("/ai/models").then(res => res.data);
}

// Sólo superadmin. El API la prueba contra Anthropic antes de guardarla.
// Returns { apiKey: { source, last4 } }
export function saveAnthropicApiKey(key) {
  return httpStore.post("/ai/api_key", { key }).then(res => res.data);
}

// Sólo superadmin: vuelve a usar la key de la variable de entorno
export function clearAnthropicApiKey() {
  return httpStore.delete("/ai/api_key").then(res => res.data);
}
